// ─── SpriteSheet Manager ──────────────────────────────────────────────────────
// Two complementary surfaces in this module:
//
//   1. `SpriteSheet` class — the stateful, in-engine sprite registry. Consumers
//      (renderer, asset-loader, character, GameCanvas) register procedurally
//      generated OffscreenCanvases and loaded <img>s under string keys and
//      blit from them every frame. Pre-existing API — do not change shape.
//
//   2. Async manifest API — `loadManifest / loadSprite / preloadAll /
//      getCachedSprite / isLoaded`. New contract: fetches the public manifest
//      JSON, loads PNGs lazily, caches them, and is SSR-safe.
//
// Manifest on disk is nested by category (tilesheets/characters/buildings/props).
// We flatten with category prefixes so keys mirror GeneratedAssets naming so
// loaded PNGs can override procedural fallbacks under the same key:
//   tilesheets.grass    -> "tile_grass"
//   characters.ceo_1    -> "char_ceo_1"
//   buildings.sales     -> "bldg_sales"
//   props.palmTree      -> "prop_palmTree"

import { TILE_SIZE } from "./constants";

// ─── SpriteSheet class (existing engine surface) ─────────────────────────────

export interface SpriteRegion {
  col: number;
  row: number;
  width: number;
  height: number;
}

export class SpriteSheet {
  private sheets: Map<string, HTMLImageElement | OffscreenCanvas> = new Map();
  private tileWidth: number;
  private tileHeight: number;

  constructor(tileWidth: number = TILE_SIZE, tileHeight: number = TILE_SIZE) {
    this.tileWidth = tileWidth;
    this.tileHeight = tileHeight;
  }

  /** Register a loaded image under a key. */
  addSheet(key: string, image: HTMLImageElement): void {
    this.sheets.set(key, image);
  }

  /** Register a procedurally generated OffscreenCanvas under a key. */
  addCanvas(key: string, canvas: OffscreenCanvas): void {
    this.sheets.set(key, canvas);
  }

  /** Check if a sheet is loaded. */
  hasSheet(key: string): boolean {
    return this.sheets.has(key);
  }

  /** Get the raw image/canvas for a sheet key. */
  getSheet(key: string): HTMLImageElement | OffscreenCanvas | undefined {
    return this.sheets.get(key);
  }

  /** Draw a sprite from a sheet onto the canvas. */
  drawSprite(
    ctx: CanvasRenderingContext2D,
    sheetKey: string,
    col: number,
    row: number,
    x: number,
    y: number,
    width?: number,
    height?: number,
  ): void {
    const sheet = this.sheets.get(sheetKey);
    if (!sheet) return;

    const sw = width ?? this.tileWidth;
    const sh = height ?? this.tileHeight;

    ctx.drawImage(
      sheet as CanvasImageSource,
      col * sw,
      row * sh,
      sw,
      sh,
      x,
      y,
      sw,
      sh,
    );
  }

  /** Draw a rectangular color placeholder when no real sprite exists. */
  drawPlaceholder(
    ctx: CanvasRenderingContext2D,
    color: string,
    x: number,
    y: number,
    width: number,
    height: number,
  ): void {
    ctx.fillStyle = color;
    ctx.fillRect(x, y, width, height);
  }

  /** Draw a character placeholder — a simple pixel-art-style figure. */
  drawCharacterPlaceholder(
    ctx: CanvasRenderingContext2D,
    color: string,
    x: number,
    y: number,
    direction: number,
    animFrame: number,
  ): void {
    // Body
    ctx.fillStyle = color;
    ctx.fillRect(x + 2, y + 10, 12, 16);

    // Head
    ctx.fillStyle = "#FDEBD0";
    ctx.fillRect(x + 4, y + 1, 8, 9);

    // Eyes based on direction
    ctx.fillStyle = "#2C3E50";
    switch (direction) {
      case 0: // Up — no eyes visible
        break;
      case 1: // Down
        ctx.fillRect(x + 5, y + 5, 2, 2);
        ctx.fillRect(x + 9, y + 5, 2, 2);
        break;
      case 2: // Left
        ctx.fillRect(x + 4, y + 5, 2, 2);
        break;
      case 3: // Right
        ctx.fillRect(x + 10, y + 5, 2, 2);
        break;
    }

    // Legs with walk animation
    ctx.fillStyle = "#2C3E50";
    const legOffset = animFrame % 2 === 0 ? 0 : 2;
    ctx.fillRect(x + 3 + legOffset, y + 26, 4, 6);
    ctx.fillRect(x + 9 - legOffset, y + 26, 4, 6);
  }

  clear(): void {
    this.sheets.clear();
  }
}

// ─── Async manifest loader (new contract) ────────────────────────────────────

const MANIFEST_URL = "/assets/surggies/manifest.json";
const PRELOAD_TIMEOUT_MS = 5000;

// 1x1 transparent PNG (base64) — fallback so callers never crash on failure.
const TRANSPARENT_PIXEL_DATA_URL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";

type Manifest = Record<string, string>;

interface NestedManifest {
  tilesheets?: Record<string, string>;
  characters?: Record<string, string>;
  buildings?: Record<string, string>;
  props?: Record<string, string>;
  [k: string]: unknown;
}

const CATEGORY_PREFIX: Record<string, string> = {
  tilesheets: "tile_",
  characters: "char_",
  buildings: "bldg_",
  props: "prop_",
};

const isBrowser = (): boolean => typeof window !== "undefined";

// Module-level caches.
let manifestPromise: Promise<Manifest> | null = null;
let manifestCache: Manifest | null = null;
const spritePromises = new Map<string, Promise<HTMLImageElement>>();
const spriteCache = new Map<string, HTMLImageElement>();

function flattenManifest(raw: unknown): Manifest {
  const out: Manifest = {};
  if (!raw || typeof raw !== "object") return out;

  const nested = raw as NestedManifest;
  const entries = Object.entries(nested);
  const hasNestedGroups = ["tilesheets", "characters", "buildings", "props"].some(
    (k) => typeof (nested as Record<string, unknown>)[k] === "object",
  );

  if (!hasNestedGroups) {
    for (const [k, v] of entries) {
      if (typeof v === "string") out[k] = v;
    }
    return out;
  }

  for (const [category, prefix] of Object.entries(CATEGORY_PREFIX)) {
    const group = nested[category as keyof NestedManifest];
    if (group && typeof group === "object") {
      for (const [k, v] of Object.entries(group as Record<string, unknown>)) {
        if (typeof v === "string") out[`${prefix}${k}`] = v;
      }
    }
  }

  return out;
}

function makeFallbackImage(): HTMLImageElement {
  const img = new Image();
  img.src = TRANSPARENT_PIXEL_DATA_URL;
  return img;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => {
      console.warn(`[sprite-sheet] failed to load image: ${src}`);
      resolve(makeFallbackImage());
    };
    img.src = src;
  });
}

export function loadManifest(): Promise<Manifest> {
  if (!isBrowser()) return Promise.resolve({});
  if (manifestCache) return Promise.resolve(manifestCache);
  if (manifestPromise) return manifestPromise;

  manifestPromise = (async () => {
    try {
      const res = await fetch(MANIFEST_URL, { cache: "force-cache" });
      if (!res.ok) {
        console.warn(
          `[sprite-sheet] manifest fetch returned ${res.status}; using empty manifest`,
        );
        manifestCache = {};
        return manifestCache;
      }
      const json = (await res.json()) as unknown;
      const flat = flattenManifest(json);
      if (Object.keys(flat).length === 0) {
        console.warn("[sprite-sheet] manifest is empty");
      }
      manifestCache = flat;
      return manifestCache;
    } catch (err) {
      console.warn("[sprite-sheet] manifest load failed:", err);
      manifestCache = {};
      return manifestCache;
    }
  })();

  return manifestPromise;
}

export function loadSprite(key: string): Promise<HTMLImageElement> {
  if (!isBrowser()) {
    return Promise.resolve({} as HTMLImageElement);
  }

  const cached = spriteCache.get(key);
  if (cached) return Promise.resolve(cached);

  const inflight = spritePromises.get(key);
  if (inflight) return inflight;

  const promise = (async () => {
    const manifest = await loadManifest();
    const url = manifest[key];
    if (!url) {
      const msg = `[sprite-sheet] missing manifest key: "${key}"`;
      console.warn(msg);
      throw new Error(msg);
    }
    const img = await loadImage(url);
    spriteCache.set(key, img);
    return img;
  })();

  spritePromises.set(key, promise);
  // On rejection, drop the in-flight entry so callers can retry.
  promise.catch(() => {
    spritePromises.delete(key);
  });
  return promise;
}

export function getCachedSprite(key: string): HTMLImageElement | null {
  return spriteCache.get(key) ?? null;
}

/**
 * Register an already-loaded image under a flat key. Lets AssetLoader feed
 * this module's cache so getCachedSprite() sees every manifest asset without
 * a second fetch.
 */
export function registerSprite(key: string, img: HTMLImageElement): void {
  spriteCache.set(key, img);
}

export function isLoaded(key: string): boolean {
  return spriteCache.has(key);
}

export async function preloadAll(keys?: string[]): Promise<void> {
  if (!isBrowser()) return;

  const manifest = await loadManifest();
  const target = keys && keys.length > 0 ? keys : Object.keys(manifest);
  if (target.length === 0) return;

  const work = Promise.all(
    target.map((k) =>
      loadSprite(k).catch((err: unknown) => {
        console.warn(`[sprite-sheet] preload skipped "${k}":`, err);
      }),
    ),
  );

  const timeout = new Promise<void>((resolve) => {
    setTimeout(() => {
      console.warn(
        `[sprite-sheet] preloadAll hit ${PRELOAD_TIMEOUT_MS}ms timeout`,
      );
      resolve();
    }, PRELOAD_TIMEOUT_MS);
  });

  await Promise.race([work.then(() => undefined), timeout]);
}
